/**
 * Guide section of the Agent tab: plain-language help for staff. The text lives
 * in guide/guideContent.js; this component lays it out, searches it and lets
 * example requests start a conversation.
 */
import React, { useMemo, useState } from 'react';
import { BarChart3, Bot, Database, FileSpreadsheet, Globe, Link2, ListChecks, Mail, MessageCircle, Search, ShieldCheck, Undo2, Wand2, X } from 'lucide-react';
import { useAgent } from '../AgentProvider';
import { toolLabel } from '../agentContextMap';
import {
  ACTION_GROUPS,
  APPROVAL_TOPICS,
  ASKING_EXAMPLES,
  ASKING_TIPS,
  BUILTIN_SKILL_GUIDES,
  CANNOT_DO,
  GLOSSARY,
  GOOD_AT,
  GUIDE_SECTIONS,
  HOW_TO_STEPS,
  INTEGRATIONS,
  INTRO,
  LIMITS,
  PROMISES,
  SAFETY_POINTS,
  SCREEN_TOUR,
  SKILL_EDITOR_FIELDS,
  SKILL_RULES,
  SKILL_USE_STEPS,
  SKILLS_INTRO,
  TROUBLESHOOTING,
  WHO_CAN_USE,
} from '../guide/guideContent';

const ICONS = { approve: ListChecks, undo: Undo2, record: ShieldCheck, database: Database, mail: Mail, message: MessageCircle, file: FileSpreadsheet, link: Link2, chart: BarChart3, bot: Bot, globe: Globe };

// Sections that stay visible while searching (the rest is reading material).
const SEARCHABLE = new Set(['actions', 'skills', 'integrations', 'help', 'glossary']);

/** Guide text with **bold** runs. The content is static, so no HTML is involved. */
export function Rich({ text }) {
  return (
    <>
      {String(text)
        .split('**')
        .map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>))}
    </>
  );
}

const searchText = (...values) =>
  values
    .flat(Infinity)
    .filter(Boolean)
    .map((value) => String(value).replace(/\*\*/g, ''))
    .join(' ')
    .toLowerCase();

/** True when every word of the query appears somewhere in the text. */
export function matchesQuery(query, text) {
  return String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word));
}

function Section({ id, title, intro, children }) {
  return (
    <section className="dagent-guide-section" id={`dagent-guide-${id}`} aria-labelledby={`dagent-guide-${id}-title`}>
      <h3 id={`dagent-guide-${id}-title`}>{title}</h3>
      {intro && (
        <p className="dagent-guide-lead">
          <Rich text={intro} />
        </p>
      )}
      {children}
    </section>
  );
}

function TryButton({ prompt, onTryPrompt, label }) {
  return (
    <button type="button" className="dagent-suggestion dagent-guide-try" onClick={() => onTryPrompt?.(prompt)} title="Start a conversation with this request">
      <Wand2 size={12} aria-hidden="true" /> {label || `“${prompt}”`}
    </button>
  );
}

function ActionTags({ action, available }) {
  return (
    <div className="dagent-guide-tags">
      {action.approval ? (
        <span className="dagent-guide-tag dagent-guide-tag--approval">Needs your approval</span>
      ) : (
        <span className="dagent-guide-tag dagent-guide-tag--auto">Runs straight away</span>
      )}
      {action.undo === 'yes' && <span className="dagent-guide-tag dagent-guide-tag--undo">Can be undone</span>}
      {action.undo === 'no' && <span className="dagent-guide-tag dagent-guide-tag--final">Cannot be undone</span>}
      {!action.undo && <span className="dagent-guide-tag dagent-guide-tag--none">Changes nothing in the PMS</span>}
      {available === false && <span className="dagent-guide-tag dagent-guide-tag--off">Not available for your role</span>}
    </div>
  );
}

function ActionCard({ action, available, onTryPrompt }) {
  return (
    <article className="dagent-guide-action" aria-label={action.title}>
      <div className="dagent-guide-action__head">
        <h5>{action.title}</h5>
        <ActionTags action={action} available={available} />
      </div>
      <p>
        <Rich text={action.what} />
      </p>
      {action.notes?.length > 0 && (
        <ul className="dagent-guide-notes">
          {action.notes.map((note) => (
            <li key={note}>
              <Rich text={note} />
            </li>
          ))}
        </ul>
      )}
      <div className="dagent-guide-examples">
        <span className="dagent-label">Try asking</span>
        {action.examples.map((example) => (
          <TryButton key={example} prompt={example} onTryPrompt={onTryPrompt} />
        ))}
      </div>
      <p className="dagent-guide-steplabel">
        Shows in the conversation as “{toolLabel(action.tool)}”
      </p>
    </article>
  );
}

function BulletList({ items, className = 'dagent-guide-list' }) {
  return (
    <ul className={className}>
      {items.map((item) => (
        <li key={item}>
          <Rich text={item} />
        </li>
      ))}
    </ul>
  );
}

function AgentGuide({ onTryPrompt, onOpenSection }) {
  const agent = useAgent();
  const [query, setQuery] = useState('');
  const searching = query.trim().length > 0;

  const metaTools = agent?.meta?.tools;
  const availableTools = useMemo(() => (Array.isArray(metaTools) && metaTools.length > 0 ? new Set(metaTools.map((tool) => tool.name)) : null), [metaTools]);

  const found = useMemo(() => {
    const groups = ACTION_GROUPS.map((group) => ({
      ...group,
      actions: group.actions.filter((action) => matchesQuery(query, searchText(group.title, action.title, action.what, action.notes, action.examples, toolLabel(action.tool)))),
    })).filter((group) => group.actions.length > 0);
    return {
      groups,
      skills: BUILTIN_SKILL_GUIDES.filter((skill) => matchesQuery(query, searchText(skill.name, skill.what, skill.changes, skill.example))),
      integrations: INTEGRATIONS.filter((item) => matchesQuery(query, searchText(item.title, item.what, item.points))),
      help: TROUBLESHOOTING.filter((item) => matchesQuery(query, searchText(item.question, item.answer))),
      glossary: GLOSSARY.filter((item) => matchesQuery(query, searchText(item.term, item.meaning))),
    };
  }, [query]);

  const actionCount = found.groups.reduce((total, group) => total + group.actions.length, 0);
  const counts = { actions: actionCount, skills: found.skills.length, integrations: found.integrations.length, help: found.help.length, glossary: found.glossary.length };
  const resultCount = Object.values(counts).reduce((total, count) => total + count, 0);
  const visible = (id) => !searching || (SEARCHABLE.has(id) && counts[id] > 0);
  const sections = GUIDE_SECTIONS.filter((section) => visible(section.id));
  const titleOf = Object.fromEntries(GUIDE_SECTIONS.map((section) => [section.id, section.title]));

  const jump = (id) => {
    document.getElementById(`dagent-guide-${id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="dagent-guide">
      <nav className="dagent-guide__toc" aria-label="Guide contents">
        <div className="dagent-kicker">Guide</div>
        {sections.map((section) => (
          <button key={section.id} type="button" onClick={() => jump(section.id)}>
            {section.title}
            {searching && <span className="dagent-guide__count">{counts[section.id]}</span>}
          </button>
        ))}
      </nav>

      <div className="dagent-guide__main">
        <div className="dagent-guide__search">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            className="dagent-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search the guide, e.g. WhatsApp, undo, check out"
            aria-label="Search the guide"
          />
          {searching && (
            <button type="button" className="dagent-icon-btn" onClick={() => setQuery('')} title="Clear search">
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
        {searching && (
          <p className="dagent-muted dagent-small" role="status">
            {resultCount === 0 ? `Nothing in the guide matches “${query.trim()}”. Try other words, or just ask the agent.` : `${resultCount} result${resultCount === 1 ? '' : 's'} for “${query.trim()}”`}
          </p>
        )}

        {visible('start') && (
          <Section id="start" title={titleOf.start}>
            <p className="dagent-guide-lead">
              <Rich text={INTRO} />
            </p>
            <div className="dagent-guide-grid dagent-guide-grid--three">
              {PROMISES.map(({ icon, title, text }) => {
                const Icon = ICONS[icon];
                return (
                  <div key={title} className="dagent-guide-promise">
                    <Icon size={18} aria-hidden="true" />
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                );
              })}
            </div>
            <h4 className="dagent-subhead">Good for</h4>
            <BulletList items={GOOD_AT} />
            <div className="dagent-banner dagent-banner--gold dagent-guide-banner">
              {/* one flex item, so the bold words keep normal spacing */}
              <span>
                <Rich text={WHO_CAN_USE} />
              </span>
            </div>
          </Section>
        )}

        {visible('how-to') && (
          <Section id="how-to" title={titleOf['how-to']}>
            <ol className="dagent-guide-steps">
              {HOW_TO_STEPS.map((step) => (
                <li key={step.title}>
                  <strong>{step.title}</strong>
                  {step.text.map((paragraph) => (
                    <p key={paragraph}>
                      <Rich text={paragraph} />
                    </p>
                  ))}
                </li>
              ))}
            </ol>
          </Section>
        )}

        {visible('asking') && (
          <Section id="asking" title={titleOf.asking} intro="The clearer the request, the better the result. Plain words are fine.">
            <BulletList items={ASKING_TIPS} />
            <h4 className="dagent-subhead">Instead of … try</h4>
            <div className="dagent-guide-grid">
              {ASKING_EXAMPLES.map((example) => (
                <div key={example.better} className="dagent-guide-card dagent-guide-compare">
                  <span className="dagent-guide-compare__instead">{example.instead}</span>
                  <TryButton prompt={example.better} onTryPrompt={onTryPrompt} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {visible('approvals') && (
          <Section id="approvals" title={titleOf.approvals}>
            <div className="dagent-guide-grid">
              {APPROVAL_TOPICS.map((topic) => (
                <div key={topic.title} className="dagent-guide-card">
                  <h5>{topic.title}</h5>
                  <p>
                    <Rich text={topic.text} />
                  </p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {visible('actions') && (
          <Section
            id="actions"
            title={titleOf.actions}
            intro={searching ? undefined : 'Everything the agent can do, grouped by task. You never need to name an action: just ask, and the agent picks the right ones. Click an example to try it.'}
          >
            {!searching && (
              <div className="dagent-guide-legend">
                <span className="dagent-guide-tag dagent-guide-tag--auto">Runs straight away</span> looks things up
                <span className="dagent-guide-tag dagent-guide-tag--approval">Needs your approval</span> waits on an approval card
                <span className="dagent-guide-tag dagent-guide-tag--undo">Can be undone</span>
                <span className="dagent-guide-tag dagent-guide-tag--final">Cannot be undone</span>
              </div>
            )}
            {found.groups.map((group) => (
              <div key={group.id} className="dagent-guide-group">
                <h4>{group.title}</h4>
                {!searching && group.intro && (
                  <p className="dagent-muted dagent-small">
                    <Rich text={group.intro} />
                  </p>
                )}
                <div className="dagent-guide-actions">
                  {group.actions.map((action) => (
                    <ActionCard key={action.tool} action={action} available={availableTools ? availableTools.has(action.tool) : undefined} onTryPrompt={onTryPrompt} />
                  ))}
                </div>
              </div>
            ))}
          </Section>
        )}

        {visible('skills') && (
          <Section id="skills" title={titleOf.skills}>
            {!searching &&
              SKILLS_INTRO.map((paragraph) => (
                <p key={paragraph} className="dagent-guide-lead">
                  <Rich text={paragraph} />
                </p>
              ))}
            <h4 className="dagent-subhead">Skills that come with the agent</h4>
            <div className="dagent-guide-grid">
              {found.skills.map((skill) => (
                <div key={skill.slug} className="dagent-guide-card">
                  <h5>{skill.name}</h5>
                  <p>{skill.what}</p>
                  <p className="dagent-muted dagent-small">{skill.changes}</p>
                  <TryButton prompt={skill.example} onTryPrompt={onTryPrompt} label="Try it" />
                </div>
              ))}
            </div>
            {!searching && (
              <>
                <h4 className="dagent-subhead">Use a skill</h4>
                <ol className="dagent-guide-list">
                  {SKILL_USE_STEPS.map((step) => (
                    <li key={step}>
                      <Rich text={step} />
                    </li>
                  ))}
                </ol>
                <h4 className="dagent-subhead">Create or change a skill</h4>
                <BulletList items={SKILL_RULES} />
                <div className="dagent-table-wrap">
                  <table className="dagent-table dagent-guide-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>What to put there</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SKILL_EDITOR_FIELDS.map((row) => (
                        <tr key={row.field}>
                          <td className="dagent-nowrap">
                            <strong>{row.field}</strong>
                          </td>
                          <td>
                            <Rich text={row.meaning} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {onOpenSection && (
                  <button type="button" className="dagent-btn dagent-btn--ghost dagent-guide-open" onClick={() => onOpenSection('skills')}>
                    Open the Skills tab
                  </button>
                )}
              </>
            )}
          </Section>
        )}

        {visible('integrations') && (
          <Section id="integrations" title={titleOf.integrations} intro={searching ? undefined : 'What the agent is connected to, and the rules each connection follows.'}>
            <div className="dagent-guide-grid">
              {found.integrations.map((item) => {
                const Icon = ICONS[item.icon];
                return (
                  <div key={item.id} className="dagent-guide-card">
                    <h5 className="dagent-guide-card__title">
                      <Icon size={16} aria-hidden="true" /> {item.title}
                    </h5>
                    <p>{item.what}</p>
                    <BulletList items={item.points} className="dagent-guide-notes" />
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {visible('screen') && (
          <Section id="screen" title={titleOf.screen}>
            <div className="dagent-guide-grid">
              {SCREEN_TOUR.map((part) => (
                <div key={part.title} className="dagent-guide-card">
                  <h5>{part.title}</h5>
                  <BulletList items={part.points} className="dagent-guide-notes" />
                </div>
              ))}
            </div>
          </Section>
        )}

        {visible('safety') && (
          <Section id="safety" title={titleOf.safety}>
            <div className="dagent-guide-grid">
              {SAFETY_POINTS.map((point) => (
                <div key={point.title} className="dagent-guide-card">
                  <h5>{point.title}</h5>
                  <p>
                    <Rich text={point.text} />
                  </p>
                </div>
              ))}
            </div>
            <h4 className="dagent-subhead">The agent cannot</h4>
            <BulletList items={CANNOT_DO} />
          </Section>
        )}

        {visible('help') && (
          <Section id="help" title={titleOf.help}>
            <div className="dagent-guide-faq">
              {found.help.map((item) => (
                <details key={item.question} open={searching}>
                  <summary>{item.question}</summary>
                  <p>
                    <Rich text={item.answer} />
                  </p>
                </details>
              ))}
            </div>
          </Section>
        )}

        {visible('limits') && (
          <Section id="limits" title={titleOf.limits} intro="Bigger jobs work too: split them into several requests.">
            <div className="dagent-table-wrap">
              <table className="dagent-table dagent-guide-table">
                <thead>
                  <tr>
                    <th>What</th>
                    <th>Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {LIMITS.map((row) => (
                    <tr key={row.what}>
                      <td>{row.what}</td>
                      <td>{row.limit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {visible('glossary') && (
          <Section id="glossary" title={titleOf.glossary}>
            <dl className="dagent-guide-glossary">
              {found.glossary.map((item) => (
                <div key={item.term}>
                  <dt>{item.term}</dt>
                  <dd>{item.meaning}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}
      </div>
    </div>
  );
}

export default AgentGuide;
